import { z } from 'zod';
export const idParam = z.object({ id: z.string().uuid() });
export const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a date as YYYY-MM-DD');
export const periodString = z.string().regex(/^\d{4}-\d{2}$/, 'Expected a period as YYYY-MM');
/** Rupees on the wire, converted to paise at the service boundary. */
export const rupees = z
    .number()
    .finite()
    .min(0, 'Amount cannot be negative')
    .max(99_999_999, 'Amount is too large');
export const signedRupees = z.number().finite().min(-99_999_999).max(99_999_999);
export const quantity = z
    .number()
    .finite()
    .min(0.001, 'Quantity must be greater than zero')
    .max(1_000_000);
export const signedQuantity = z
    .number()
    .finite()
    .refine((v) => v !== 0, 'Quantity must not be zero')
    .refine((v) => Math.abs(v) <= 1_000_000, 'Quantity is too large');
export const gstRate = z
    .number()
    .refine((v) => [0, 5, 12, 18, 28].includes(v), 'GST rate must be 0, 5, 12, 18 or 28');
export const percent = z.number().finite().min(0).max(100);
export const shortText = z.string().trim().min(1, 'Required').max(120);
export const optionalText = z.string().trim().max(240).optional().or(z.literal(''));
export const noteText = z.string().trim().max(500).optional().or(z.literal(''));
export const gstin = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Not a valid 15-character GSTIN');
export const optionalGstin = z.union([gstin, z.literal('')]).optional();
export const phone = z
    .string()
    .trim()
    .regex(/^[+]?[0-9 ()-]{6,18}$/, 'Not a valid phone number');
export const optionalPhone = z.union([phone, z.literal('')]).optional();
/**
 * An Indian postal code.
 *
 * Six digits, and never a leading zero - the first digit is the postal region
 * and there is no region nine... or zero. It is a mandatory field on an
 * e-invoice, which is why it is mandatory here.
 */
export const pincode = z
    // Named, so leaving it out reads as a missing PIN code rather than as Zod
    // telling a hotelier it "expected string, received undefined".
    .string({ error: 'Enter the 6-digit PIN code' })
    .trim()
    .regex(/^[1-9][0-9]{5}$/, 'Enter the 6-digit PIN code');
export const pagination = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * A true/false flag on a query string.
 *
 * Not `z.coerce.boolean()`, which is `Boolean(value)` - and `Boolean('false')`
 * is true, so `?flag=false` switched the filter on rather than off. A query
 * string carries words, so the words are read as words, and a word that means
 * neither is refused rather than guessed at. A bare `?flag` is true, which is
 * how people write flags.
 */
const TRUTHY = new Set(['true', '1', 'yes', 'on', '']);
const FALSY = new Set(['false', '0', 'no', 'off']);
export const booleanQuery = z.union([z.boolean(), z.string()]).transform((value, ctx) => {
    if (typeof value === 'boolean')
        return value;
    const word = value.trim().toLowerCase();
    if (TRUTHY.has(word))
        return true;
    if (FALSY.has(word))
        return false;
    ctx.addIssue({ code: 'custom', message: 'Expected true or false' });
    return z.NEVER;
});
export const searchQuery = z.object({
    q: z.string().trim().max(80).optional(),
});
//# sourceMappingURL=common.js.map